<template>
  <div class="two-factor-auth__container">
    <button
      type="button"
      class="two-factor-auth__back-btn"
      @click="backToLogin">
      {{ $gettext('Back') }}
    </button>
    <div class="two-factor-auth__header">
      <div class="two-factor__image"></div>
      <h3 class="two-factor-auth__title">{{ $gettext('Enter verification code') }}</h3>
      <p class="two-factor-auth__subtitle">{{ subtitleText }}</p>
    </div>
    <form class="two-factor-auth__form"
          @submit.prevent.stop="submitForm">
      <div class="two-factor-auth__code-input">
        <div class="two-factor-auth__code-inputs">
          <sk-input
            v-for="(digit, index) in 6"
            :key="index"
            :ref="`codeInput${index}`"
            :preselected-value="verificationDigits[index]"
            :type="'text'"
            :max-length="1"
            :reg-exp="/[^0-9]/g"
            :mob-numeric-keyboard="true"
            :disabled="progressActive"
            :validation-name="codeError ? 'verificationCode' : ''"
            class="two-factor-auth__digit-input"
            :class="{ 'two-factor-auth__digit-input--error': codeError }"
            @fieldvaluechanged="handleDigitInput(index, $event)"
            @input="handleDigitInput(index, $event)" />
        </div>
        <div v-if="codeError"
             class="two-factor-auth__error">
          <span class="error-message">
            {{ codeError }}
          </span>
        </div>
      </div>
      <div class="two-factor-auth__resend">
        <button
          type="button"
          class="two-factor-auth__resend-btn"
          :class="{ 'two-factor-auth__resend-btn--disabled': countdown > 0 }"
          :disabled="countdown > 0 || progressActive"
          @click="resendCode">
          {{ resendButtonText }}
        </button>
      </div>
      <div class="two-factor-auth__actions">
        <button
          type="submit"
          class="sk-btn sk-btn--default two-factor-auth__submit-btn"
          :class="{ 'two-factor-auth__submit-btn--loading': progressActive }"
          :disabled="!isFormValid || progressActive">
          <span v-if="progressActive"
                class="two-factor-auth__spinner"></span>
          {{ progressActive ? $gettext('Verifying...') : $gettext('Continue') }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
  import {mapGetters, mapActions, mapState} from 'vuex';

  export default {
    name: 'two-factor-auth',
    data() {
      return {
        verificationDigits: ['', '', '', '', '', ''],
        codeError: '',
        progressActive: false,
        countdown: 0,
        countdownInterval: null,
        eventHandlers: []
      };
    },
    computed: {
      ...mapGetters('TTAuthStore', [
        'isTwoFactorRequired',
        'getTwoFactorType',
        'isAuthSessionExpired',
        'getNextChallengeAvailableAt',
        'getMaskedDestination',
        'getChallengeSequence'
      ]),
      ...mapGetters('UserInfoStore', [
        'userIsManager'
      ]),
      ...mapState({
        mobileApp: (state) => state.mobileApp
      }),
      verificationCode() {
        return this.verificationDigits.join('');
      },
      isFormValid() {
        const allDigitsFilled = this.verificationDigits.every((digit) => digit !== '');
        const allDigitsNumeric = this.verificationDigits.every((digit) => /^\d$/.test(digit));
        return allDigitsFilled && allDigitsNumeric;
      },
      canResend() {
        if (!this.getNextChallengeAvailableAt) return true;
        return new Date() >= new Date(this.getNextChallengeAvailableAt);
      },
      maskedDestination() {
        return this.getMaskedDestination || '';
      },
      challengeSequence() {
        return this.getChallengeSequence || '';
      },
      resendButtonText() {
        if (this.countdown > 0) {
          return `${this.$gettext('Resend in')} ${this.formatCountdown(this.countdown)}`;
        }
        return this.$gettext('Resend code');
      },
      subtitleText() {
        const template = this.$gettext('Please, enter the code #%{sequence} we sent to your phone number %{destination}.');
        return this.$gettextInterpolate(template, {sequence: this.challengeSequence, destination: this.maskedDestination});
      }
    },
    watch: {
      mobileApp(newVal, oldVal) {
        // If mobile app state changes during 2FA process, ensure proper handling
        if (newVal && newVal !== oldVal && this.progressActive) {
          // If we're in the middle of verification and mobile app state changes,
          // give a bit more time for the process to complete
          setTimeout(() => {
            if (this.progressActive) {
              this.progressActive = false;
              this.codeError = this.$gettext('Please try again. Mobile app state changed during verification.');
            }
          }, 2000);
        }
      }
    },
    methods: {
      ...mapActions('TTAuthStore', [
        'sendTwoFactorCode',
        'verifyTwoFactorCode',
        'logIn',
        'clearTwoFactorDataFromStorage'
      ]),
      handleDigitInput(index, value) {
        // Only allow numeric input
        if (!/^\d*$/.test(value)) {
          this.$set(this.verificationDigits, index, '');
          return;
        }

        // Limit to single digit
        this.$set(this.verificationDigits, index, value.slice(-1));

        // Clear error when user starts typing
        this.clearErrors();

        // Auto-focus next input if current input has a value
        if (value && index < 5) {
          this.$nextTick(() => {
            const nextInputElement = this.$refs[`codeInput${index + 1}`][0].$el.querySelector('input');
            if (nextInputElement) {
              nextInputElement.focus();
            }
          });
        }
      },
      handleKeydown(index, event) {
        // Handle backspace to go to previous input
        if (event.key === 'Backspace' && !this.verificationDigits[index] && index > 0) {
          const prevInputElement = this.$refs[`codeInput${index - 1}`][0].$el.querySelector('input');
          if (prevInputElement) {
            prevInputElement.focus();
          }
        }
      },
      handlePaste(event) {
        event.preventDefault();
        const pastedData = event.clipboardData.getData('text');
        const digits = pastedData.replace(/\D/g, '').slice(0, 6);

        // Fill the inputs with pasted digits
        let i = 0;
        while (i < 6) {
          this.$set(this.verificationDigits, i, digits[i] || '');
          i += 1;
        }

        // Focus the next empty input or the last input
        const nextEmptyIndex = this.verificationDigits.findIndex((digit) => digit === '');
        const focusIndex = nextEmptyIndex >= 0 ? nextEmptyIndex : 5;
        this.$nextTick(() => {
          const focusInputElement = this.$refs[`codeInput${focusIndex}`][0].$el.querySelector('input');
          if (focusInputElement) {
            focusInputElement.focus();
          }
        });
      },
      handleFocus(index) {
        // Select all text when focusing on an input
        this.$nextTick(() => {
          const inputElement = this.$refs[`codeInput${index}`][0].$el.querySelector('input');
          if (inputElement) {
            inputElement.select();
          }
        });
      },
      clearErrors() {
        this.codeError = '';
      },
      validateForm() {
        this.codeError = '';

        if (!this.verificationCode) {
          this.codeError = this.$gettext('Please enter the verification code');
          return false;
        }

        if (this.verificationCode.length !== 6) {
          this.codeError = this.$gettext('Verification code must be 6 digits');
          return false;
        }

        if (!/^\d{6}$/.test(this.verificationCode)) {
          this.codeError = this.$gettext('Verification code must contain only numbers');
          return false;
        }

        return true;
      },
      async submitForm() {
        if (!this.validateForm()) return;

        this.progressActive = true;

        try {
          const response = await this.verifyTwoFactorCode(this.verificationCode);

          // Clear 2FA data and proceed with normal login
          this.clearTwoFactorDataFromStorage();

          // Login with the received token
          await this.logIn({
            token: response.token,
            uid: response.identity.uid,
            rememberMe: true
          });

          // Set user info and redirect
          this.$store.commit('UserInfoStore/setUserInfo', {userInfoObj: {uid: response.identity.uid}});
          this.$store.commit('TTAuthStore/setAuthInfoToStorage', {
            token: response.token,
            uid: response.identity.uid
          });

          // Fetch user info and redirect based on user type
          await this.$store.dispatch('UserInfoStore/fetchUserInfo', response.identity.uid);

          // Handle mobile app case - reload page like in AuthSignInForm
          if (this.$store.state.mobileApp) {
            setTimeout(() => {
              window.location.reload();
            }, 500);
            return;
          }

          this.redirectBasedOnUserType();
        } catch (error) {
          this.progressActive = false;

          if (error && error.errors && error.errors.verificationToken) {
            this.codeError = this.$gettext('Verification code is not valid. Please, try again.');
          } else if (error && error.errors && error.errors.global) {
            if (error.errors.global === 'Only 1 code per minute is allowed') {
              this.codeError = this.$gettext('Only one code per minute is allowed.');
            } else {
              this.codeError = this.$gettext('Locked for 2 minutes after 3 failed attempts.');
            }
          } else {
            this.codeError = this.$gettext('An error occurred. Please try again.');
          }
        }
      },
      async resendCode() {
        if (!this.canResend || this.progressActive) return;

        this.progressActive = true;

        try {
          await this.sendTwoFactorCode();
          this.startCountdown();
          this.verificationDigits = ['', '', '', '', '', ''];
          this.clearErrors();
        } catch (error) {
          if (error && error.errors && error.errors.global) {
            this.codeError = error.errors.global;
          } else {
            this.codeError = this.$gettext('An error occurred. Please try again.');
          }
        } finally {
          this.progressActive = false;
        }
      },
      startCountdown() {
        if (this.countdownInterval) {
          clearInterval(this.countdownInterval);
        }

        if (this.getNextChallengeAvailableAt) {
          const nextAvailable = new Date(this.getNextChallengeAvailableAt);
          const now = new Date();
          const diff = Math.max(0, Math.ceil((nextAvailable - now) / 1000));

          this.countdown = diff;

          this.countdownInterval = setInterval(() => {
            this.countdown -= 1;
            if (this.countdown <= 0) {
              clearInterval(this.countdownInterval);
              this.countdownInterval = null;
            }
          }, 1000);
        }
      },
      formatCountdown(seconds) {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
      },
      redirectBasedOnUserType() {
        if (this.userIsManager) {
          this.$router.replace(this.$makeRoute({
            name: 'ManagerHome',
          }));
        } else {
          this.$router.replace(this.$makeRoute({
            name: 'ServerHome'
          }));
        }
      },
      setSkillsNeedDialect() {
        const skillsNeedDialect = this.$store.state.TTManageSupplierSkill.skillsNeedDialect;
        if (skillsNeedDialect?.length) {
          localStorage.setItem('skillsNeedDialect', JSON.stringify(skillsNeedDialect));
          this.$store.commit('ModalStore/setModal', {
            component: 'dialects-modal',
          });
        }
      },
      backToLogin() {
        this.clearTwoFactorDataFromStorage();
        this.$router.replace(this.$makeRoute({name: 'SignIn'}));
      },
      checkAuthSession() {
        if (this.isAuthSessionExpired) {
          this.$store.commit('InfoModalStore/setInfoModal', {
            text: this.$gettext('Your authentication session has expired. Please log in again.')
          });
          return false;
        }
        return true;
      },
      handleVisibilityChange() {
        // Handle mobile app visibility changes
        if (this.mobileApp && document.visibilityState === 'visible' && this.progressActive) {
          // If app becomes visible again and we're still in progress,
          // check if we need to reset the state
          setTimeout(() => {
            if (this.progressActive) {
              // If still in progress after 3 seconds, something might be stuck
              this.progressActive = false;
              this.codeError = this.$gettext('Verification seems to be stuck. Please try again.');
            }
          }, 3000);
        }
      }
    },
    async mounted() {
      // Load 2FA data from localStorage
      await this.$store.dispatch('TTAuthStore/loadTwoFactorDataFromStorage');

      // Check if we have valid 2FA session
      if (!this.isTwoFactorRequired || !this.checkAuthSession()) {
        this.backToLogin();
        return;
      }

      // For mobile apps, add a small delay to ensure proper state synchronization
      if (this.mobileApp) {
        await new Promise((resolve) => setTimeout(resolve, 100));

        // Add visibility change listener for mobile app handling
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
      }

      // Check if we have a valid countdown time that hasn't passed yet
      if (this.getNextChallengeAvailableAt && new Date() < new Date(this.getNextChallengeAvailableAt)) {
        // Start countdown if we have a valid future time
        this.startCountdown();
      } else if (!this.$store.getters['TTAuthStore/getChallengeToken']) {
        // Send initial code if we don't have challenge data and no valid countdown
        await this.resendCode();
      } else {
        // Start countdown if we have existing challenge data
        this.startCountdown();
      }

      // Add event listeners to sk-input components
      this.$nextTick(() => {
        for (let i = 0; i < 6; i += 1) {
          const inputElement = this.$refs[`codeInput${i}`][0].$el.querySelector('input');
          if (inputElement) {
            const keydownHandler = (event) => this.handleKeydown(i, event);
            const pasteHandler = (event) => this.handlePaste(event);
            const focusHandler = () => this.handleFocus(i);

            inputElement.addEventListener('keydown', keydownHandler);
            inputElement.addEventListener('paste', pasteHandler);
            inputElement.addEventListener('focus', focusHandler);

            this.eventHandlers.push({
              element: inputElement,
              keydownHandler,
              pasteHandler,
              focusHandler
            });
          }
        }
      });
    },
    beforeDestroy() {
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
      }

      // Remove event listeners from sk-input components
      this.eventHandlers.forEach((handler) => {
        handler.element.removeEventListener('keydown', handler.keydownHandler);
        handler.element.removeEventListener('paste', handler.pasteHandler);
        handler.element.removeEventListener('focus', handler.focusHandler);
      });
      this.eventHandlers = [];

      // Remove visibility change listener
      if (this.mobileApp) {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      }
    }
  };
</script>

<style scoped>
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.two-factor-auth__back-btn {
  position: absolute;
  top: 24px;
  left: 24px;
  z-index: 2;
  padding: 0 0 0 20px;
  border: none;
  background: none;
  background-image: url(~@assets/imgs/arrows/arrow_to_left_black.svg);
  background-position: 0 50%;
  background-size: 16px 16px;
  background-repeat: no-repeat;
  color: #222;
  font-weight: 500;
  font-size: 14px;
  transition: color 0.2s ease;
}

.two-factor-auth__back-btn:hover {
  color: #000;
}

.two-factor-auth__container {
  position: relative;
  width: 640px;
  padding: 40px 24px;
}

.two-factor-auth__header {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  text-align: center;
}

.two-factor__image {
  width: 96px;
  height: 96px;
  background-image: url(~@assets/imgs/salita/secure/two_factor_image.png);
  background-position: 50% 50%;
  background-repeat: no-repeat;
}

.two-factor-auth__title {
  margin: 24px 0 8px 0;
  color: #333;
  font-weight: 600;
  font-size: 20px;
}

.two-factor-auth__subtitle {
  margin: 0;
  color: #333;
  font-size: 14px;
  line-height: 1.5;
  text-align: center;
}

.two-factor-auth__form {
  margin-bottom: 30px;
}

.two-factor-auth__code-input {
  margin-bottom: 24px;
}

.two-factor-auth__code-inputs {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.two-factor-auth__digit-input {
  width: 36px !important;
  margin-bottom: 0 !important;
  text-align: center;
}

.two-factor-auth__digit-input .sk-input__input:focus {
  border-color: var(--color-primary);
  border-color: var(--color-secondary);
  outline: none;
}

.two-factor-auth__digit-input--error .sk-input__input {
  border: 2px solid var(--color-error-600);
  background-color: var(--color-error-100);
}

.two-factor-auth__digit-input .sk-input__description {
  display: none;
}

.two-factor-auth__error {
  margin-top: 8px;
  color: #dc3545;
  font-size: 12px;
  text-align: center;
}

.two-factor-auth__resend {
  margin: 16px 0 24px 0;
  text-align: center;
}

.two-factor-auth__resend-btn {
  padding: 0;
  border: none;
  background: none;
  color: #333;
  font-size: 14px;
  line-height: 1.5;
  cursor: pointer;
  transition: color 0.2s ease;
}

.two-factor-auth__resend-btn:hover:not(:disabled) {
  color: var(--color-primary);
  color: var(--color-secondary);
}

.two-factor-auth__resend-btn:disabled {
  color: #999;
  cursor: not-allowed;
}

.two-factor-auth__resend-btn--disabled {
  color: #999;
  cursor: not-allowed;
}

.two-factor-auth__actions {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
}

.two-factor-auth__submit-btn {
  width: fit-content;
  padding: 0 70px;
}

.two-factor-auth__submit-btn:hover:not(:disabled) {
  background-color: #e54d1a;
}

.two-factor-auth__submit-btn:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.two-factor-auth__submit-btn--loading {
  position: relative;
}

.two-factor-auth__spinner {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  border: 2px solid transparent;
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@media (max-width: 768px) {
  .two-factor-auth__container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
  }
}

@media (max-width: 480px) {
  .two-factor-auth__container {
    margin: 20px auto;
    padding: 30px 20px;
  }

  .two-factor-auth__title {
    font-size: 18px;
  }

  .two-factor-auth__code-inputs {
    gap: 6px;
  }

  .two-factor-auth__digit-input {
    width: 45px !important;
  }

  .two-factor-auth__digit-input .sk-input__input {
    width: 45px;
    height: 45px;
    font-size: 18px;
  }
}
</style>
